import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin, Rss, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSettings } from "@/lib/useSettings";
import { BRAND } from "@/lib/dataleo";

const Footer = () => {
  const { lang } = useLang();
  const { settings } = useSettings();
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  return (
    <footer data-testid="site-footer" className="bg-[#0E1F1A] text-[#F6F3EE]">
      {/* Final CTA band - "Un projet ?" style from Citwell. */}
      <section className="border-b border-[#1F2A26]">
        <div className="dl-container py-20 sm:py-24 grid lg:grid-cols-[auto_minmax(0,1fr)] gap-10 lg:gap-20 items-start">
          <div className="shrink-0">
            <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
              {lang === "fr" ? "Un projet ?" : "A project?"}
            </div>
            <h2 className="font-display text-5xl sm:text-6xl mt-4 leading-[0.95]">
              {lang === "fr" ? "Parlons-en." : "Let's talk."}
            </h2>
          </div>
          <div className="max-w-2xl">
            <p className="text-[17px] sm:text-[18px] leading-[1.6] text-white/75">
              {lang === "fr"
                ? "Transformation Supply Chain, programme APS, Decision App à cadrer, montée en charge d'une équipe projet - nous échangeons sur vos priorités et les étapes possibles."
                : "Supply Chain transformation, APS program, Decision App to frame, or project-team ramp-up - let us discuss your priorities and next steps."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="footer-contact-cta" className="dl-pill dl-pill-primary">
                {lang === "fr" ? "Contacter Dataleo" : "Talk to Dataleo"}
                <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
              <Link to="/capabilities" className="dl-pill dl-pill-outline-light">
                {lang === "fr" ? "Nos capacités" : "Our capabilities"}
                <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Columns */}
      <div className="dl-container py-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#00A878]">Dataleo</div>
          <h3 className="font-display text-2xl sm:text-3xl mt-4 leading-tight max-w-sm">
            {BRAND.primary}
          </h3>
          <p className="mt-4 text-[13.5px] text-white/60 max-w-md leading-relaxed">
            {lang === "fr" ? BRAND.conviction.fr : BRAND.conviction.en}
          </p>

          <address className="mt-6 text-[13px] not-italic text-white/70 space-y-2">
            <div className="flex items-start gap-2" data-testid="footer-city">
              <MapPin size={14} className="mt-0.5 text-[#00A878] shrink-0" />
              <span>Lyon, France</span>
            </div>
            {(settings.contact_email || "contact@dataleo.com") && (
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-[#00A878]" />
                <a href={`mailto:${settings.contact_email || "contact@dataleo.com"}`} className="hover:text-white font-bold-dl">
                  {settings.contact_email || "contact@dataleo.com"}
                </a>
              </div>
            )}
          </address>

          {(() => {
            const linkedinDataleo = settings.linkedin_dataleo_url || "https://www.linkedin.com/company/dataleo/";
            return (
              <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
                <a
                  href={linkedinDataleo}
                  target="_blank"
                  rel="noopener noreferrer me"
                  data-testid="social-linkedin-dataleo"
                  aria-label="LinkedIn Dataleo"
                  className="w-10 h-10 grid place-items-center rounded-full border border-[#1F2A26] hover:border-[#00A878] hover:text-[#00A878] transition-colors"
                >
                  <Linkedin size={16} />
                </a>
                {settings.linkedin_jfn_url && (
                  <a
                    href={settings.linkedin_jfn_url}
                    target="_blank"
                    rel="noopener noreferrer me"
                    data-testid="social-linkedin-jfn"
                    aria-label="LinkedIn Jean-François Nordmann"
                    className="w-10 h-10 grid place-items-center rounded-full border border-[#1F2A26] hover:border-[#00A878] hover:text-[#00A878] transition-colors text-[10px] font-black-dl"
                  >
                    JFN
                  </a>
                )}
              </div>
            );
          })()}
        </div>

        <div>
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-white/60">
            {lang === "fr" ? "Corporate" : "Corporate"}
          </div>
          <ul className="mt-4 space-y-2.5 text-[14px] font-bold-dl">
            <li><Link to="/capabilities" className="hover:text-[#00A878]">{lang === "fr" ? "Capacités" : "Capabilities"}</Link></li>
            <li><Link to="/clients" className="hover:text-[#00A878]">{lang === "fr" ? "Clients" : "Clients"}</Link></li>
            <li><Link to="/partners" className="hover:text-[#00A878]">{lang === "fr" ? "Partenaires" : "Partners"}</Link></li>
            <li><Link to="/about" className="hover:text-[#00A878]">{lang === "fr" ? "À propos" : "About"}</Link></li>
            <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-[#00A878]">Contact</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-white/60">Radar</div>
          <ul className="mt-4 space-y-2.5 text-[14px] font-bold-dl">
            <li><Link to="/radar" className="hover:text-[#00A878]">{lang === "fr" ? "Accueil Radar" : "Radar home"}</Link></li>
            <li><Link to="/radar/news" className="hover:text-[#00A878]">News</Link></li>
            <li><Link to="/radar/jobs" className="hover:text-[#00A878]">Jobs</Link></li>
            <li><Link to="/radar/alerts" className="hover:text-[#00A878]">{lang === "fr" ? "Alertes" : "Alerts"}</Link></li>
            <li><Link to="/radar/insights" className="hover:text-[#00A878]">Insights</Link></li>
            <li><Link to="/radar/tutorials" className="hover:text-[#00A878]">Tutorials</Link></li>
            <li><Link to="/radar/ecosystem" data-testid="footer-ecosystem-link" className="hover:text-[#00A878]">{lang === "fr" ? "Écosystème" : "Ecosystem"}</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-white/60">Labs</div>
          <ul className="mt-4 space-y-2.5 text-[14px] font-bold-dl">
            <li><Link to="/labs" className="hover:text-[#00A878]">{lang === "fr" ? "Tous les projets" : "All projects"}</Link></li>
          </ul>
          <div className="mt-8 pt-6 border-t border-[#1F2A26] space-y-2 text-[11px] font-mono-dl">
            <a href={`${apiBase}/api/rss.xml`} target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#00A878] inline-flex items-center gap-1">
              <Rss size={11} /> RSS
            </a>
            <br />
            <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="text-white/60 hover:text-[#00A878]">sitemap.xml</a>
          </div>
        </div>
      </div>

      <div className="border-t border-[#1F2A26]" data-nosnippet>
        <div className="dl-container py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-[12px] text-white/50">
          <div>© {new Date().getFullYear()} Dataleo. {lang === "fr" ? "Tous droits réservés." : "All rights reserved."}</div>
          <div className="font-mono-dl tracking-wider">{BRAND.territory}</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
